import "@/index.css";
import "@/lib/dayjsConfig";
import "@/lib/i18n";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { LoaderScreen } from "@/components/common/LoaderScreen";
import { MobileWarningModal } from "@/components/navigation/MobileWarningModal";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { isPublicRoute } from "@/config/routes";
import { ComposerDraftMaintenance } from "@/features/composer-drafts/ComposerDraftMaintenance";
import { NotFoundPage } from "@/features/error-pages/components/NotFoundPage";
import { useGlobalLogoutListener } from "@/hooks/useGlobalLogoutListener";
import { useLanguageSync } from "@/hooks/useLanguageSync";
import { queryClient } from "@/lib/queryClient";
import { navigateAfterDraftFlush } from "@/lib/safeAppNavigation";
import { initSentry } from "@/lib/sentry";
import AuthProvider, { useAuthContext } from "@/providers/AuthProvider";
import { UploadQuotaDialogProvider } from "@/providers/UploadQuotaDialogProvider";
import { routeTree } from "@/routeTree.gen";

// ChunkLoadErrorの処理
let isChunkReloadPending = false;

const handleChunkError = (error: Error) => {
  console.error("Chunk load error detected:", error);

  if (
    error.name === "ChunkLoadError" ||
    error.message.includes("Loading chunk") ||
    error.message.includes("Failed to fetch dynamically imported module")
  ) {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.includes("tanstack_router")) {
        sessionStorage.removeItem(key);
      }
    });

    if (!isChunkReloadPending) {
      isChunkReloadPending = true;
      void navigateAfterDraftFlush({
        reason: "chunk_load_recovery",
        navigate: () => window.location.reload(),
      });
    }
    return;
  }

  throw error;
};

// カスタムエラーコンポーネント
const CustomErrorComponent: React.FC<{ error: Error }> = ({ error }) => {
  useEffect(() => {
    handleChunkError(error);
  }, [error]);

  return <LoaderScreen />;
};

// ルーターインスタンスを作成
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultErrorComponent: CustomErrorComponent,
  defaultNotFoundComponent: NotFoundPage,
  defaultPendingComponent: LoaderScreen,
});

initSentry(router);

// グローバルエラーハンドリング
window.addEventListener("unhandledrejection", (event) => {
  if (
    event.reason?.message?.includes(
      "Failed to fetch dynamically imported module",
    ) ||
    event.reason?.name === "ChunkLoadError"
  ) {
    event.preventDefault();
    handleChunkError(event.reason);
  }
});

// 型安全性のためのモジュール拡張
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// 認証が必要なルート用のラッパー
const AuthenticatedApp: React.FC = () => {
  const { isAuthReady } = useAuthContext();

  // 各種フックを使用
  useGlobalLogoutListener();
  useLanguageSync();

  if (!isAuthReady) {
    return <LoaderScreen />;
  }

  return (
    <div className="min-h-screen font-primary">
      {" "}
      <MobileWarningModal />
      <RouterProvider router={router} />
      <Toaster />
    </div>
  );
};

// 認証不要ルート用のラッパー
const PublicApp: React.FC = () => {
  // 各種フックを使用
  useLanguageSync();

  return (
    <div className="min-h-screen font-primary">
      <RouterProvider router={router} />
      <Toaster />
    </div>
  );
};

// メインアプリケーション
const App: React.FC = () => {
  const currentPath = window.location.pathname;

  if (isPublicRoute(currentPath)) {
    return (
      <QueryClientProvider client={queryClient}>
        <UploadQuotaDialogProvider>
          <TooltipProvider delayDuration={150}>
            <PublicApp />
          </TooltipProvider>
        </UploadQuotaDialogProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <UploadQuotaDialogProvider>
        <TooltipProvider delayDuration={150}>
          <AuthProvider>
            <AuthenticatedApp />
          </AuthProvider>
        </TooltipProvider>
      </UploadQuotaDialogProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

// アプリケーションをレンダリング
const rootElement = document.getElementById("root");
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement, {
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    <StrictMode>
      <ComposerDraftMaintenance>
        <App />
      </ComposerDraftMaintenance>
    </StrictMode>,
  );
}
